import {
  Alert,
  AppShellContent,
  Button,
  Group,
  Headline,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@assetwatch/ui";
import { WorkRole } from "@assetwatch/work-authorization";
import { useEffect, useRef, useState } from "react";
import { ApproveWorkRequestModal } from "../components/ApproveWorkRequestModal";
import { WorkPage, WorkSplitView } from "../components/layout/WorkPageLayout";
import {
  PageViewAccessDenied,
  PageViewLoadFailure,
  PageViewUnavailable,
} from "../components/page-views/PageViewStates";
import { DeleteRequestDialog } from "../components/snowflakes/requests/DeleteRequestDialog";
import { NewWorkRequestSideSheet } from "../components/snowflakes/requests/NewWorkRequestSideSheet";
import { RequestDetails } from "../components/snowflakes/requests/RequestDetails";
import { RequestFilters } from "../components/snowflakes/requests/RequestFilters";
import { RequestQueue } from "../components/snowflakes/requests/RequestQueue";
import { SearchFilterToolbar } from "../components/snowflakes/SearchFilterToolbar";
import { useWorkScope } from "../components/WorkScopeProvider";
import { canPerformAction } from "../lib/access";
import type { RequestsPageView } from "../lib/requestsPageView";
import { type WorkRequestId, workRequestIdSchema } from "../lib/workIds";
import { useMarkWorkRequestViewedMutation } from "../mutations/useMarkWorkRequestViewedMutation";
import { useDeleteWorkRequestMutation } from "../mutations/useWorkRequestMutations";
import { useNewWorkRequestCountQuery } from "../queries/useNewWorkRequestCountQuery";
import { useWorkRequestQuery } from "../queries/useWorkRequestQuery";
import { useWorkRequestsQuery } from "../queries/useWorkRequestsQuery";
import type { WorkRequest } from "../types/workRequest";

type RequestView =
  | "all"
  | "pending"
  | "approved"
  | "done"
  | "rejected"
  | "unassigned";

const requestStatusByView = {
  approved: "APPROVED",
  done: "CANCELED",
  pending: "PENDING",
  rejected: "DECLINED",
} as const;

const resolvedStatusRank: Record<WorkRequest["status"]["code"], number> = {
  PENDING: 0,
  APPROVED: 1,
  DECLINED: 2,
  CANCELED: 2,
  DELETED: 3,
};

type LegacyRequestFilters = {
  assetFilter: string[];
  priorities: string[];
  requestView: RequestView;
  search: string;
  statuses: string[];
  submittedFrom?: string;
  submittedTo?: string;
};

function filterLegacyRequests(
  requests: WorkRequest[],
  filters: LegacyRequestFilters,
) {
  const normalizedSearch = filters.search.trim().toLowerCase();
  return requests.filter((request) => {
    const matchesView =
      filters.requestView === "all" ||
      (filters.requestView === "pending" &&
        request.status.code === "PENDING") ||
      (filters.requestView === "approved" &&
        request.status.code === "APPROVED") ||
      (filters.requestView === "done" && request.status.code === "CANCELED") ||
      (filters.requestView === "rejected" &&
        request.status.code === "DECLINED") ||
      (filters.requestView === "unassigned" && request.asset === null);
    const matchesSearch =
      normalizedSearch.length === 0 ||
      [String(request.id), request.requestNumber, request.title].some((value) =>
        value.toLowerCase().includes(normalizedSearch),
      );
    const matchesPriority =
      filters.priorities.length === 0 ||
      (request.priority !== null &&
        filters.priorities.includes(request.priority.code));
    const matchesAsset =
      filters.assetFilter.length === 0 ||
      (request.asset !== null &&
        filters.assetFilter.includes(request.asset.publicId));
    const matchesStatus =
      filters.statuses.length === 0 ||
      filters.statuses.includes(request.status.code);
    const requestDate = request.submittedAt.slice(0, 10);
    const matchesSubmittedDate =
      (!filters.submittedFrom || requestDate >= filters.submittedFrom) &&
      (!filters.submittedTo || requestDate <= filters.submittedTo);

    return (
      matchesView &&
      matchesSearch &&
      matchesPriority &&
      matchesAsset &&
      matchesStatus &&
      matchesSubmittedDate
    );
  });
}

function getLegacyCounts(
  requests: WorkRequest[],
  currentUserCognitoId?: string,
) {
  return {
    mine: requests.filter(
      (request) => request.requester.cognitoId === currentUserCognitoId,
    ).length,
    all: requests.length,
    awaiting: requests.filter((request) => request.status.code === "PENDING")
      .length,
    approved: requests.filter((request) => request.status.code === "APPROVED")
      .length,
    done: requests.filter((request) => request.status.code === "CANCELED")
      .length,
    rejected: requests.filter((request) => request.status.code === "DECLINED")
      .length,
    high: requests.filter((request) => request.priority?.code === "HIGH")
      .length,
    unassigned: requests.filter((request) => request.asset === null).length,
  };
}

function isRequestView(value: string): value is RequestView {
  return [
    "all",
    "pending",
    "approved",
    "done",
    "rejected",
    "unassigned",
  ].includes(value);
}

type RequestsScreenProps = {
  linkedRequestId?: WorkRequestId;
  pageView?: RequestsPageView;
  onPageViewChange?: (
    patch: Partial<RequestsPageView>,
    options?: { replace?: boolean },
  ) => void;
};

function RequestsScreen({
  linkedRequestId,
  pageView,
  onPageViewChange,
}: RequestsScreenProps = {}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const isFacilityAdministrator =
    selectedFacility?.role === WorkRole.ADMINISTRATOR;
  const canCreateRequest = Boolean(
    selectedFacility && canPerformAction(selectedFacility, "CREATE_REQUEST"),
  );
  const deleteWorkRequestMutation = useDeleteWorkRequestMutation();
  const markWorkRequestViewedMutation = useMarkWorkRequestViewedMutation();
  // A deep link opens its Request immediately, without a click through the
  // list  -  that still counts as the user opening the record.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally re-fires only when the linked request id changes, not on every mutate identity change.
  useEffect(() => {
    if (linkedRequestId === undefined) return;
    markWorkRequestViewedMutation.mutate(linkedRequestId);
  }, [linkedRequestId]);

  const [pendingDeleteIds, setPendingDeleteIds] = useState<WorkRequestId[]>([]);
  const [selectedRequestId, setSelectedRequestId] =
    useState<WorkRequestId | null>(null);
  const previousLinkedRequestId = useRef(linkedRequestId);
  useEffect(() => {
    if (previousLinkedRequestId.current === linkedRequestId) return;
    previousLinkedRequestId.current = linkedRequestId;
    setSelectedRequestId(linkedRequestId ?? null);
  }, [linkedRequestId]);
  // null = no explicit choice yet; a deep link defaults to "all" so the
  // linked request is present in the list, and any tab click wins after that.
  const [requestView, setRequestView] = useState<RequestView | null>(
    pageView?.view ?? null,
  );
  const effectiveRequestView = requestView ?? "all";
  const linkedRequestQuery = useWorkRequestQuery(linkedRequestId);
  const [search, setSearch] = useState(pageView?.search ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(
    pageView?.search ?? "",
  );
  const [priorities, setPriorities] = useState<string[]>(
    pageView?.priority ?? [],
  );
  const [requestedBy, setRequestedBy] = useState<string[]>(
    pageView?.requester ?? [],
  );
  const [statuses, setStatuses] = useState<string[]>(pageView?.status ?? []);
  const [submittedFrom, setSubmittedFrom] = useState<string | undefined>(
    pageView?.submittedFrom,
  );
  const [submittedTo, setSubmittedTo] = useState<string | undefined>(
    pageView?.submittedTo,
  );
  const [assetFilter, setAssetFilter] = useState<string[]>(
    pageView?.asset ?? [],
  );
  const previousPageView = useRef(pageView);
  useEffect(() => {
    const previous = previousPageView.current;
    if (previous?.request !== pageView?.request) {
      setSelectedRequestId(pageView?.request ?? null);
    }
    if (previous?.view !== pageView?.view) {
      setRequestView(pageView?.view ?? null);
    }
    if (previous?.search !== pageView?.search) {
      setSearch(pageView?.search ?? "");
      setDebouncedSearch(pageView?.search ?? "");
    }
    if (
      JSON.stringify(previous?.priority) !== JSON.stringify(pageView?.priority)
    ) {
      setPriorities(pageView?.priority ?? []);
    }
    if (
      JSON.stringify(previous?.requester) !==
      JSON.stringify(pageView?.requester)
    ) {
      setRequestedBy(pageView?.requester ?? []);
    }
    if (JSON.stringify(previous?.status) !== JSON.stringify(pageView?.status)) {
      setStatuses(pageView?.status ?? []);
    }
    if (previous?.submittedFrom !== pageView?.submittedFrom) {
      setSubmittedFrom(pageView?.submittedFrom);
    }
    if (previous?.submittedTo !== pageView?.submittedTo) {
      setSubmittedTo(pageView?.submittedTo);
    }
    if (JSON.stringify(previous?.asset) !== JSON.stringify(pageView?.asset)) {
      setAssetFilter(pageView?.asset ?? []);
    }
    previousPageView.current = pageView;
  }, [pageView]);
  const [isAddWorkRequestOpen, setIsAddWorkRequestOpen] = useState(false);
  const [approveRequestId, setApproveRequestId] =
    useState<WorkRequestId | null>(null);
  const requestFilters = {
    view:
      effectiveRequestView === "pending"
        ? ("awaiting" as const)
        : ("all" as const),
    search: debouncedSearch,
    priorities,
    requestedBy: isFacilityAdministrator ? requestedBy : [],
    statuses: statuses,
    tabStatus:
      requestStatusByView[
        effectiveRequestView as keyof typeof requestStatusByView
      ],
    submittedFrom,
    submittedTo,
    assetAssignment:
      effectiveRequestView === "unassigned"
        ? ("unassigned" as const)
        : undefined,
    assetIds: assetFilter,
  };
  const desiredPageCount = pageView?.requestPages ?? 1;
  const restorationKey = JSON.stringify([
    selectedCustomer?.id,
    selectedFacility?.id,
    selectedFacility?.role,
    requestFilters,
  ]);
  const [manualRestoration, setManualRestoration] = useState<{
    key: string;
    pageCount: number;
  } | null>(null);
  // Keep the previous automatic restore budget; explicit Show more actions
  // may continue past it without making an arbitrary URL a bulk-load command.
  const restorationPageCount =
    manualRestoration?.key === restorationKey &&
    manualRestoration.pageCount === desiredPageCount
      ? desiredPageCount
      : Math.min(desiredPageCount, 100);
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isRefetchError,
    isFetchNextPageError,
    isFetchingNextPage,
    isFetching,
    isPending,
    refetch,
  } = useWorkRequestsQuery(requestFilters, {
    pageCount: onPageViewChange ? desiredPageCount : undefined,
  });
  useEffect(() => {
    if (
      (data?.pageCount ?? 1) < restorationPageCount &&
      hasNextPage &&
      !isFetching &&
      !isFetchNextPageError
    ) {
      void fetchNextPage();
    }
  }, [
    data?.pageCount,
    restorationPageCount,
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetching,
  ]);
  const newestRequestId = data?.requests[0]?.id;
  const newWorkRequestCountQuery = useNewWorkRequestCountQuery(
    requestFilters,
    newestRequestId,
  );
  const newWorkRequestCount = newWorkRequestCountQuery.data ?? 0;
  const linkedRequestNeedsLoad = Boolean(
    linkedRequestId &&
      linkedRequestQuery.isPending &&
      !data?.requests.some((request) => request.id === linkedRequestId),
  );

  // A broken deep link (wrong facility, deleted request, stale URL) must not
  // take down the whole screen  -  surface it inline and keep the list usable.
  const linkedRequestFailed =
    Boolean(linkedRequestId) && linkedRequestQuery.isError;

  const tabStatus =
    requestStatusByView[
      effectiveRequestView as keyof typeof requestStatusByView
    ];
  const requests = (data?.requests ?? []).filter(
    (request) => isFacilityAdministrator || request.status.code !== "DELETED",
  );
  const hasConflictingStatusFilter =
    tabStatus !== undefined &&
    statuses.length > 0 &&
    !statuses.includes(tabStatus);
  const visibleRequests = data?.serverFiltered
    ? hasConflictingStatusFilter
      ? []
      : requests.filter(
          (request) =>
            tabStatus === undefined || request.status.code === tabStatus,
        )
    : filterLegacyRequests(requests, {
        assetFilter,
        priorities,
        requestView: effectiveRequestView,
        search,
        statuses,
        submittedFrom,
        submittedTo,
      });
  const pendingRequests = visibleRequests.filter(
    (request) => request.status.code === "PENDING",
  );
  // Ordered the way the queue shows them (Approved, then Done or Declined,
  // then Deleted) so the default selection is the first resolved card shown.
  const resolvedRequests = visibleRequests
    .filter((request) => request.status.code !== "PENDING")
    .sort(
      (left, right) =>
        resolvedStatusRank[left.status.code] -
        resolvedStatusRank[right.status.code],
    );
  // A queue click overrides the deep link. The linked query result is only a
  // fallback while the deep link's initial view is untouched  -  once the user
  // applies a view, filter, or search, the visible list is the sole source so
  // the details pane cannot show a request outside the chosen view.
  const effectiveSelectedRequestId = selectedRequestId ?? linkedRequestId;
  const hasSupersededDeepLink =
    requestView !== null ||
    search.trim().length > 0 ||
    priorities.length > 0 ||
    requestedBy.length > 0 ||
    submittedFrom !== undefined ||
    submittedTo !== undefined ||
    statuses.length > 0 ||
    assetFilter.length > 0;
  const selectedRequest =
    visibleRequests.find(
      (request) => request.id === effectiveSelectedRequestId,
    ) ??
    (!hasSupersededDeepLink && effectiveSelectedRequestId === linkedRequestId
      ? linkedRequestQuery.data
      : undefined) ??
    pendingRequests[0] ??
    resolvedRequests[0];

  useEffect(() => {
    if (
      linkedRequestId !== undefined ||
      selectedRequestId !== null ||
      selectedRequest === undefined
    ) {
      return;
    }
    setSelectedRequestId(selectedRequest.id);
    onPageViewChange?.({ request: selectedRequest.id }, { replace: true });
  }, [linkedRequestId, onPageViewChange, selectedRequest, selectedRequestId]);

  if ((isPending && !data) || linkedRequestNeedsLoad) {
    return (
      <AppShellContent pending>
        <span />
      </AppShellContent>
    );
  }

  if (isError && !data) {
    return (
      <AppShellContent>
        {error instanceof Error && /: 403$/.test(error.message) ? (
          <PageViewAccessDenied />
        ) : (
          <PageViewLoadFailure onRetry={() => void refetch()} />
        )}
      </AppShellContent>
    );
  }

  const statusOptions = (data?.metadata?.statuses ?? []).map(
    ({ code, name }) => ({ label: name, value: code }),
  );
  const counts = data?.serverFiltered
    ? {
        ...(data.metadata?.counts ?? {
          mine: 0,
          all: 0,
          awaiting: 0,
          approved: 0,
          done: 0,
          rejected: 0,
          high: 0,
        }),
        unassigned: requests.filter((request) => request.asset === null).length,
      }
    : getLegacyCounts(requests);
  const requesterOptions = (data?.metadata?.requesters ?? []).map(
    ({ cognitoId, name }) => ({ label: name, value: cognitoId }),
  );
  const activeFilterCount =
    priorities.length +
    (isFacilityAdministrator ? requestedBy.length : 0) +
    statuses.length +
    assetFilter.length +
    Number(submittedFrom !== undefined) +
    Number(submittedTo !== undefined);
  // Clear Filters also resets the view and search, so those count too.
  const hasActiveFilters =
    activeFilterCount > 0 ||
    search.trim().length > 0 ||
    effectiveRequestView !== "all";

  function clearFilters() {
    setRequestView(null);
    setSearch("");
    setDebouncedSearch("");
    setPriorities([]);
    setRequestedBy([]);
    setStatuses([]);
    setAssetFilter([]);
    setSubmittedFrom(undefined);
    setSubmittedTo(undefined);
    setSelectedRequestId(null);
    onPageViewChange?.({
      request: undefined,
      view: undefined,
      search: undefined,
      asset: undefined,
      priority: [],
      requester: [],
      status: [],
      submittedFrom: undefined,
      submittedTo: undefined,
    });
  }

  function handleDeleteSelected(requestIds: WorkRequestId[]) {
    setPendingDeleteIds(requestIds);
  }

  function handleSelectRequest(requestId: WorkRequestId) {
    setSelectedRequestId(requestId);
    markWorkRequestViewedMutation.mutate(requestId);
    onPageViewChange?.({ request: requestId });
  }

  async function confirmDeleteSelected() {
    try {
      await Promise.all(
        pendingDeleteIds.map((workRequestId) =>
          deleteWorkRequestMutation.mutateAsync({
            requestNumber:
              visibleRequests.find((request) => request.id === workRequestId)
                ?.requestNumber ?? "Work Request",
            workRequestId,
          }),
        ),
      );
    } catch {
      return;
    }
    if (
      selectedRequestId !== null &&
      pendingDeleteIds.some((requestId) => requestId === selectedRequestId)
    ) {
      setSelectedRequestId(null);
    }
    setPendingDeleteIds([]);
  }

  function applyView(view: RequestView) {
    setRequestView(view);
    setPriorities([]);
    setRequestedBy([]);
    setStatuses([]);
    setSubmittedFrom(undefined);
    setSubmittedTo(undefined);
    setAssetFilter([]);
    onPageViewChange?.({
      view,
      asset: undefined,
      priority: [],
      requester: [],
      status: [],
      submittedFrom: undefined,
      submittedTo: undefined,
    });
  }

  return (
    <>
      <AppShellContent>
        <WorkPage
          header={
            <Group justify="between" wrap="wrap">
              <Headline as="h1" size="lg">
                Requests
              </Headline>
              {canCreateRequest ? (
                <Button
                  icon="plus-regular"
                  onClick={() => setIsAddWorkRequestOpen(true)}
                  size="sm"
                >
                  New request
                </Button>
              ) : null}
            </Group>
          }
          alerts={
            newWorkRequestCount > 0 ||
            newWorkRequestCountQuery.isError ||
            (isRefetchError && data) ||
            linkedRequestFailed ? (
              <Stack gap={8}>
                {newWorkRequestCount > 0 ? (
                  <Alert
                    title={`${newWorkRequestCount} new request${newWorkRequestCount > 1 ? "s" : ""} available.`}
                    tone="informative"
                    action={
                      <Button
                        onClick={() => void refetch()}
                        size="sm"
                        variant="outline"
                      >
                        Refresh
                      </Button>
                    }
                  />
                ) : newWorkRequestCountQuery.isError ? (
                  <Alert
                    title="Unable to check for new requests."
                    tone="warning"
                    action={
                      <Button
                        onClick={() => void newWorkRequestCountQuery.refetch()}
                        size="sm"
                        variant="outline"
                      >
                        Retry
                      </Button>
                    }
                  />
                ) : null}

                {isRefetchError && data ? (
                  <Alert
                    title="Showing data as of the last successful refresh."
                    tone="warning"
                    action={
                      <Button
                        onClick={() => void refetch()}
                        size="sm"
                        variant="outline"
                      >
                        Retry
                      </Button>
                    }
                  />
                ) : null}

                {linkedRequestFailed ? <PageViewUnavailable /> : null}
              </Stack>
            ) : null
          }
          tabs={
            <Tabs
              onValueChange={(value: string) => {
                if (isRequestView(value)) applyView(value);
              }}
              value={effectiveRequestView}
            >
              <TabsList aria-label="Request views">
                <TabsTrigger chipLabel={counts.awaiting} value="pending">
                  Pending
                </TabsTrigger>
                <TabsTrigger chipLabel={counts.approved} value="approved">
                  Approved
                </TabsTrigger>
                <TabsTrigger chipLabel={counts.done} value="done">
                  Done
                </TabsTrigger>
                <TabsTrigger chipLabel={counts.rejected} value="rejected">
                  Rejected
                </TabsTrigger>
                <TabsTrigger chipLabel={counts.all} value="all">
                  All
                </TabsTrigger>
              </TabsList>
            </Tabs>
          }
          toolbar={
            <SearchFilterToolbar
              onSearch={(value) => {
                setDebouncedSearch(value);
                onPageViewChange?.({ search: value.trim() || undefined });
              }}
              onSearchChange={(value) => setSearch(value)}
              searchAriaLabel="Search requests"
              searchPlaceholder="Search Requests"
              searchValue={search}
            >
              <RequestFilters
                activeFilterCount={activeFilterCount}
                assetFilter={assetFilter}
                canReset={hasActiveFilters}
                onAssetChange={(value) => {
                  setAssetFilter(value);
                  onPageViewChange?.({ asset: value });
                }}
                onClearAll={clearFilters}
                onPriorityChange={(value) => {
                  setPriorities(value);
                  onPageViewChange?.({ priority: value });
                }}
                onRequesterChange={(value) => {
                  setRequestedBy(value);
                  onPageViewChange?.({ requester: value });
                }}
                onSubmittedFromChange={(value) => {
                  setSubmittedFrom(value);
                  onPageViewChange?.({ submittedFrom: value });
                }}
                onSubmittedToChange={(value) => {
                  setSubmittedTo(value);
                  onPageViewChange?.({ submittedTo: value });
                }}
                onStatusChange={(value) => {
                  setStatuses(value);
                  onPageViewChange?.({ status: value });
                }}
                assetOptions={(data?.metadata?.assets ?? []).map((asset) => ({
                  label: asset.assetCode
                    ? `${asset.name} (${asset.assetCode})`
                    : asset.name,
                  value: asset.id,
                }))}
                priorities={priorities}
                requestedBy={requestedBy}
                submittedFrom={submittedFrom}
                submittedTo={submittedTo}
                requesters={requesterOptions}
                showRequesters={isFacilityAdministrator}
                statusFilter={statuses}
                statuses={statusOptions}
              />
            </SearchFilterToolbar>
          }
        >
          <WorkSplitView
            layout="requests"
            list={
              <RequestQueue
                canDeleteRequests={isFacilityAdministrator}
                hasNextPage={hasNextPage ?? false}
                isFetchNextPageError={isFetchNextPageError}
                isFetchingNextPage={isFetchingNextPage}
                onShowMore={() => {
                  if (isFetchNextPageError || !onPageViewChange) {
                    if (!isFetching) void fetchNextPage();
                  } else {
                    const nextPageCount =
                      Math.min(desiredPageCount, data?.pageCount ?? 1) + 1;
                    setManualRestoration({
                      key: restorationKey,
                      pageCount: nextPageCount,
                    });
                    onPageViewChange({ requestPages: nextPageCount });
                  }
                }}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearFilters}
                onDeleteSelected={handleDeleteSelected}
                onSelect={handleSelectRequest}
                pendingRequests={pendingRequests}
                resolvedRequests={resolvedRequests}
                selectedRequestId={selectedRequest?.id}
              />
            }
            detail={
              <RequestDetails
                key={selectedRequest?.id ?? "empty"}
                onApprove={setApproveRequestId}
                canReview={isFacilityAdministrator}
                onDelete={(requestId) => setPendingDeleteIds([requestId])}
                originListSearch={pageView}
                request={selectedRequest}
              />
            }
          />
        </WorkPage>
      </AppShellContent>
      <DeleteRequestDialog
        isPending={deleteWorkRequestMutation.isPending}
        onConfirm={() => void confirmDeleteSelected()}
        onOpenChange={(open) => {
          if (!open && !deleteWorkRequestMutation.isPending)
            setPendingDeleteIds([]);
        }}
        open={pendingDeleteIds.length > 0}
        requestCount={pendingDeleteIds.length}
        requestNumber={
          pendingDeleteIds.length === 1
            ? (visibleRequests.find(
                (request) => request.id === pendingDeleteIds[0],
              )?.requestNumber ?? "Work Request")
            : undefined
        }
        requestTitle={
          pendingDeleteIds.length === 1
            ? visibleRequests.find(
                (request) => request.id === pendingDeleteIds[0],
              )?.title
            : undefined
        }
      />
      {canCreateRequest ? (
        <NewWorkRequestSideSheet
          onOpenChange={setIsAddWorkRequestOpen}
          onViewRequests={(requestId) => {
            const parsedRequestId = workRequestIdSchema.safeParse(requestId);
            if (!parsedRequestId.success) return;
            clearFilters();
            setSelectedRequestId(parsedRequestId.data);
            onPageViewChange?.({ request: parsedRequestId.data });
          }}
          open={isAddWorkRequestOpen}
        />
      ) : null}
      {approveRequestId !== null ? (
        <ApproveWorkRequestModal
          key={approveRequestId}
          initialRequestId={approveRequestId}
          initialRequest={selectedRequest}
          onClose={() => setApproveRequestId(null)}
          originListSearch={pageView}
          open
        />
      ) : null}
    </>
  );
}

export { RequestsScreen };
